import { useMemo } from 'react';
import { AnalyticsGatewayV1 } from '@frontend/api-analytics-gateway';
import { useTranslation } from '@frontend/i18n';
import { useAlert } from '@frontend/design-system';
import { trendLineDemoResponse } from '../common-demo-data/trend-line';
import { commonApiOptions } from '../helpers/api-helpers';
import { useRoiRequestPayload, UseRoiRequestProps } from './use-roi-request-payload';

interface UseFetchRoiTrendLine {
  isLoading?: boolean;
  trendLine?: number[];
}

export const useFetchRoiTrendLine = (props: UseRoiRequestProps): UseFetchRoiTrendLine => {
  const alert = useAlert();
  const { t } = useTranslation('analytics');
  const { request, skipRequest } = useRoiRequestPayload(props);

  const { data: trendLineResponse, isLoading } = AnalyticsGatewayV1.Queries.useGetTrendlineQuery({
    request,
    options: {
      ...commonApiOptions,
      enabled: !skipRequest,
      onError: () => {
        alert.error(t("Couldn't fetch the ROI trend details. Please try again."));
      },
    },
  });

  const trendLine: UseFetchRoiTrendLine['trendLine'] = useMemo(() => {
    const response = props.isDemoAccount ? trendLineDemoResponse : trendLineResponse;
    const values = response?.data?.map(({ count = 0 }) => count);

    // The default [0, 0] helpes to render a straight line when there is no value available
    return values?.length ? values : [0, 0];
  }, [props.isDemoAccount, trendLineResponse]);

  return {
    isLoading,
    trendLine,
  };
};
